'use client';

import { useQuery } from '@tanstack/react-query';

import {
  ThumbnailSizes,
  type ThumbnailSizeKey,
} from '../constants/thumbnail-sizes';
import {
  getDocumentSignedUrlAction,
  getPdfThumbnailUrlAction,
} from '../server/actions';
import type { Document } from '../types';
import {
  canGenerateThumbnail,
  isPdfFile,
  supportsNativeThumbnail,
} from '../utils/file-type-utils';

export const documentUrlKeys = {
  all: ['document-urls'] as const,
  url: (bucket: string, path: string) =>
    [...documentUrlKeys.all, bucket, path] as const,
  thumbnail: (bucket: string, path: string, size: ThumbnailSizeKey) =>
    [...documentUrlKeys.all, 'thumbnail', bucket, path, size] as const,
  pdfThumbnail: (bucket: string, path: string) =>
    [...documentUrlKeys.all, 'pdf-thumbnail', bucket, path] as const,
};

export interface UseDocumentUrlOptions {
  document: Document;
  thumbnailSize?: ThumbnailSizeKey;
  enabled?: boolean;
}

export function useDocumentUrl({
  document,
  thumbnailSize = 'MEDIUM',
  enabled = true,
}: UseDocumentUrlOptions) {
  const hasPreFetchedUrl = !!document.signedUrl;
  const hasPreFetchedThumbnail = !!document.thumbnailUrl;

  const urlQuery = useQuery({
    queryKey: documentUrlKeys.url(document.bucket, document.storagePath),
    queryFn: async () => {
      const result = await getDocumentSignedUrlAction({
        bucket: document.bucket,
        path: document.storagePath,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to get document URL');
      }
      return result.url;
    },
    enabled: enabled && !hasPreFetchedUrl,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  const supportsThumbnail = canGenerateThumbnail(document.mimeType);
  const isImage = supportsNativeThumbnail(document.mimeType);
  const isPdf = isPdfFile(document.mimeType);

  // Image thumbnail query (uses Supabase Image Transforms)
  const imageThumbnailQuery = useQuery({
    queryKey: documentUrlKeys.thumbnail(
      document.bucket,
      document.storagePath,
      thumbnailSize,
    ),
    queryFn: async () => {
      const size = ThumbnailSizes[thumbnailSize];
      const result = await getDocumentSignedUrlAction({
        bucket: document.bucket,
        path: document.storagePath,
        transform: size,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to get thumbnail URL');
      }
      return result.url;
    },
    enabled: enabled && isImage && !hasPreFetchedThumbnail,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  // PDF thumbnail query (uses Edge Function)
  const pdfThumbnailQuery = useQuery({
    queryKey: documentUrlKeys.pdfThumbnail(
      document.bucket,
      document.storagePath,
    ),
    queryFn: async () => {
      const size = ThumbnailSizes[thumbnailSize];
      const result = await getPdfThumbnailUrlAction({
        bucket: document.bucket,
        path: document.storagePath,
        width: size.width,
        height: size.height,
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to get PDF thumbnail URL');
      }
      return result.url;
    },
    enabled: enabled && isPdf && !hasPreFetchedThumbnail,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  // Determine which thumbnail URL to use (prefer pre-fetched)
  const thumbnailUrl = hasPreFetchedThumbnail
    ? document.thumbnailUrl
    : isImage
      ? imageThumbnailQuery.data
      : isPdf
        ? pdfThumbnailQuery.data
        : undefined;

  const isThumbnailLoading = hasPreFetchedThumbnail
    ? false
    : isImage
      ? imageThumbnailQuery.isLoading
      : isPdf
        ? pdfThumbnailQuery.isLoading
        : false;

  const thumbnailError = hasPreFetchedThumbnail
    ? null
    : isImage
      ? imageThumbnailQuery.error
      : isPdf
        ? pdfThumbnailQuery.error
        : null;

  return {
    url: hasPreFetchedUrl ? document.signedUrl! : urlQuery.data,
    thumbnailUrl,
    isLoading: hasPreFetchedUrl ? false : urlQuery.isLoading,
    isThumbnailLoading,
    error: hasPreFetchedUrl ? null : urlQuery.error,
    thumbnailError,
    supportsThumbnail,
    isPdf,
    isImage,
  };
}
